import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

function Reset() {
  const rawBaseUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.marclife.io/api/admin';
  const baseurl = rawBaseUrl.trim();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Prefill email from navigation state or sessionStorage
    const stateEmail = location.state?.email;
    const storedEmail = sessionStorage.getItem('resetEmail');
    if (stateEmail) {
      setEmail(stateEmail);
    } else if (storedEmail) {
      setEmail(storedEmail);
    }
  }, [location.state]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      toast.warn('Please enter your email address.');
      return;
    }
    if (!otp.trim()) {
      toast.warn('Please enter the OTP verification code.');
      return;
    }
    if (password.length < 6) {
      toast.warn('Password must be at least 6 characters long.');
      return;
    }
    if (password !== confirmPassword) {
      toast.warn('New password and confirmation password do not match.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${baseurl}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          otp: otp.trim(),
          password,
          confirm_password: confirmPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status) {
        toast.success(data.message || 'Password reset successfully!');
        sessionStorage.removeItem('resetEmail');
        sessionStorage.removeItem('demoOtp');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      } else {
        const msg = data.message || 'Failed to reset password. Please check your OTP and try again.';
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      const msg = 'Network error. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!email.trim()) {
      toast.warn('Please enter your email to resend OTP.');
      return;
    }
    setResending(true);
    try {
      const response = await fetch(`${baseurl}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await response.json();
      if (response.ok && data.status) {
        toast.success(data.message || 'New OTP sent to your email!');
      } else {
        toast.error(data.message || 'Could not resend OTP.');
      }
    } catch (err) {
      toast.error('Network error while resending OTP.');
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container-fluid custom-page">
        <div className="row bg-white">
          {/* The image half */}
          <div className="col-md-6 col-lg-6 col-xl-7 d-none d-md-flex bg-primary-transparent-3">
            <div className="row w-100 mx-auto text-center">
              <div className="col-md-12 col-lg-12 col-xl-12 my-auto mx-auto w-100">
                <img
                  src="assets/images/backgroud/loginImage.png"
                  className="my-auto ht-xl-80p wd-md-100p wd-xl-80p mx-auto"
                  alt="logo"
                />
              </div>
            </div>
          </div>

          {/* The content half */}
          <div className="col-md-6 col-lg-6 col-xl-5 bg-white py-4">
            <div className="login d-flex align-items-center py-2">
              <div className="container p-0">
                <div className="row">
                  <div className="col-md-10 col-lg-10 col-xl-9 mx-auto">
                    <div className="card-sigin">
                      <div className="mb-4 d-flex">
                        <Link to="/" className="header-logo">
                          <img
                            src="assets/images/logo/walletIcon.png"
                            style={{ height: '40px' }}
                            alt="logo"
                          />
                        </Link>
                      </div>

                      <div className="main-signup-header">
                        <h3 className="fw-bold text-dark">Reset Password</h3>
                        <p className="text-muted mb-4 fs-14">
                          Enter the 6-digit OTP code sent to your email along with your new password.
                        </p>

                        {error && (
                          <div className="alert alert-danger py-2 fs-13 mb-3">
                            <i className="fa fa-exclamation-circle me-1"></i> {error}
                          </div>
                        )}

                        <form onSubmit={handleResetPassword}>
                          {/* Email */}
                          <div className="form-group mb-3">
                            <label className="form-label fw-semibold text-dark">Admin Email</label>
                            <div className="input-group">
                              <span className="input-group-text bg-light text-muted">
                                <i className="fa fa-envelope"></i>
                              </span>
                              <input
                                className="form-control"
                                placeholder="name@domain.com"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          {/* OTP Code */}
                          <div className="form-group mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <label className="form-label fw-semibold text-dark mb-0">6-Digit Verification Code (OTP)</label>
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 text-decoration-none text-primary fs-12"
                                onClick={handleResendOtp}
                                disabled={resending}
                              >
                                {resending ? 'Sending...' : 'Resend Code'}
                              </button>
                            </div>
                            <div className="input-group">
                              <span className="input-group-text bg-light text-muted">
                                <i className="fa fa-key"></i>
                              </span>
                              <input
                                className="form-control"
                                placeholder="e.g. 123456"
                                type="text"
                                maxLength="6"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                required
                              />
                            </div>
                          </div>

                          {/* New Password */}
                          <div className="form-group mb-3">
                            <label className="form-label fw-semibold text-dark">New Password</label>
                            <div className="input-group">
                              <span className="input-group-text bg-light text-muted">
                                <i className="fa fa-lock"></i>
                              </span>
                              <input
                                className="form-control"
                                placeholder="Minimum 6 characters"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                minLength="6"
                                required
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                          </div>

                          {/* Confirm Password */}
                          <div className="form-group mb-4">
                            <label className="form-label fw-semibold text-dark">Confirm New Password</label>
                            <div className="input-group">
                              <span className="input-group-text bg-light text-muted">
                                <i className="fa fa-shield-alt"></i>
                              </span>
                              <input
                                className="form-control"
                                placeholder="Re-enter new password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                minLength="6"
                                required
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              >
                                <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                          </div>

                          <button
                            type="submit"
                            className="btn btn-primary btn-block w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                Updating Password...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-check-circle"></i> Save & Reset Password
                              </>
                            )}
                          </button>
                        </form>

                        <div className="main-signin-footer mt-4 pt-2 border-top text-center">
                          <p className="text-muted mb-0 fs-13">
                            Back to{' '}
                            <Link to="/login" className="text-primary fw-bold">
                              Sign In
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Reset;